<template>
  <div v-if="!pagination" class="loading">Loading...</div>
  <div v-else class="min-h-screen">
    <div class="header-bg"></div>
    <div class="max-w-screen-2xl mx-auto mt-10 pb-20 px-5 sm:px-10">
      <div class="flex space-x-5 justify-start mb-10">
        <router-link to="/">
          <div class="font-bold text-lg">首頁</div>
        </router-link>
        <p class="text-lg">></p>
        <h1 class="text-lg text-color-blue font-bold text-left">搜尋結果</h1>
      </div>

      <!-- search result map -->
      <div class="border-t-2 border-b-2 border-gray-300">
        <ResultMap ref="resultMap" style="z-index: 0" />
      </div>

      <div>
        <!-- search result info & tools -->
        <div
          v-if="pagination"
          class="p-3"
        >
          <div class="text-color-blue text-xl font-bold text-left mt-10 mb-5">
            <span v-html="searchConditionDescription" />
          <span
            v-if="pagination.data.length > 0"
            class=""
          >
            顯示第&nbsp;&nbsp;{{ startItemNumber }}&nbsp;&nbsp;到&nbsp;&nbsp;{{
              endItemNumber
            }}&nbsp;&nbsp;筆共&nbsp;&nbsp;{{
              pagination.meta.pagination.total
            }}&nbsp;&nbsp;筆資料
          </span>
          <span v-else class="">
            沒有符合搜尋條件的結果
          </span>
          </div>

          <div class="md:flex md:space-x-5 items-center mt-5 xl:mt-0">
            <div class="flex space-x-5">
              <!-- button to switch the search result in list or thumbnail mode -->
              <div class="flex space-x-2 items-center">
                <p class="text-lg hidden lg:block">顯示模式：</p>
                <button @click="switchToListMode(true)">
                  <img v-if="isListMode" alt="清單模式" src="@/assets/list.svg" class="w-9"/>
                  <img v-if="!isListMode" alt="按下清單模式" src="@/assets/list-hover.svg" class="w-9" />
                </button>
                <button @click="switchToListMode(false)">
                  <img v-if="!isListMode" alt="縮圖模式" src="@/assets/thumbnail.svg" class="w-9" />
                  <img v-if="isListMode" alt="按下縮圖模式" src="@/assets/thumbnail-hover.svg" class="w-9" />
                </button>
              </div>

              <!-- sort -->
              <div
                class="border border-black h-8 w-36 sm:w-48 md:w-40 px-2 pt-1 mt-0.5"
              >
                <select
                  id="sort"
                  name="sort"
                  class="w-32 sm:w-44 md:w-36"
                  v-model="sortBy"
                  @change="sortByChanged($event)"
                >
                  <option selected disabled>選擇排序方式</option>
                  <option value="revised_taxon_display">學名</option>
                  <option value="revised_taxon_tw">中名</option>
                  <option value="catalog_number">館號</option>
                  <option value="record_number">採集編號</option>
                  <option value="event_date_day">採集日期</option>
                  <option value="country">國家</option>
                  <option value="county">縣市</option>
                  <option value="verbatim_locality">採集地點</option>
                </select>
              </div>
            </div>

            <div class="flex space-x-5 mt-5 md:mt-0">
              <!-- button to go to advanced search page -->
              <button
                class="table-button w-32 sm:w-36"
                @click="gotoAdvancedSearchPage"
              >
                修改搜尋條件
              </button>

              <!-- button to download the search result -->
              <button
                class="table-button w-28 sm:w-36"
                @click="showDownloadResultDialog"
              >
                下載資料
              </button>
            </div>
          </div>
        </div>

        <!-- search result  -->
        <div v-if="isListMode">
          <DataList :pagination="pagination" />
        </div>
        <div v-else>
          <DataThumbnail :pagination="pagination" />
        </div>

        <!-- search result pagination -->
        <Pagination
          ref="paginationComponent"
          :pagination="pagination"
          @changePageNumber="pageChanged"
        />
      </div>
    </div>

    <!-- 下載搜尋結果視窗 -->
    <transition name="slide-fade">
      <ExportDialog
        v-if="exportOpen"
        @close="closeDialog"
        class="exportDialog z-50 w-full"
      />
    </transition>

    <div
      v-if="exportOpen"
      class="fixed top-0 left-0 z-40 bg-gray-500 opacity-50 w-full h-full"
    ></div>
  </div>
  <div class="footer-bg"></div>
</template>

<script>
import ExportDialog from "@/views/search/ExportDialog.vue";
import DataList from "@/views/search/DataList.vue";
import DataThumbnail from "@/views/search/DataThumbnail.vue";
import Pagination from "@/views/search/Pagination.vue";
import ResultMap from "@/views/search/ResultMap.vue";

export default {
  components: {
    ExportDialog,
    DataList,
    DataThumbnail,
    Pagination,
    ResultMap,
  },

  data() {
    return {
      exportOpen: false,
      isListMode: true,
      sortBy: "catalog_number",
      pagination: null,
      isGetAllPositions: true,
      maxPositionCounts: 50000,
    };
  },

  computed: {
    startItemNumber() {
      return (
        this.pagination.meta.pagination.per_page *
          (this.pagination.meta.pagination.current_page - 1) +
        1
      );
    },

    endItemNumber() {
      if (
        this.pagination.meta.pagination.current_page ===
        this.pagination.meta.pagination.total_pages
      ) {
        return this.pagination.meta.pagination.total;
      } else {
        return (
          this.pagination.meta.pagination.per_page *
          this.pagination.meta.pagination.current_page
        );
      }
    },

    searchConditionDescription() {
      const queryFields = this.$store.getters["search/queryFields"]

      const fieldLabels = {
        catalog_number: '館號',
        country_code: '國家代碼',
        country: '國家名稱',
        event_day: '採集日',
        event_month: '採集月',
        event_year: '採集年',
        family: 'Family',
        genus: 'Genus',
        identified_by: '原始鑑定者',
        include_gps: '具有座標',
        include_no_img: '具有影像',
        infraspecific_epithet: 'Infraspecific Epithet',
        is_accept_synonym: '包含異名與接受名',
        latin_name: 'Taxon Name',
        not_include_taiwan: '不含台灣',
        record_number: '採集編號',
        recorded_by: '紀錄者/採集者',
        search_only_wild: '野生植株',
        search_with_type: '模式標本',
        specific_epithet: 'Specific Epithet',
        state_tw: '州省',
        td_chinese_name: '中名',
        township: '鄉鎮',
        verbatim_locality: '採集地點',
        simple_search_name: '學名/俗名'
      }

      const conditionStrings = []
      for (const [key, value] of Object.entries(fieldLabels)) {
        if (key in queryFields) {
          if (key === 'country_code' && queryFields[key] === 'WORLD') {
            continue
          } else if (key === 'include_no_img') {
            if (queryFields[key] === false) {
              conditionStrings.push('<span style="margin-right:5px">' + value + '</span>')
            } else {
              continue
            }
          } else if (typeof queryFields[key] === 'boolean') {
            if (queryFields[key] === true) {
              conditionStrings.push('<span style="margin-right:5px">' + value + '</span>')
            }
          } else {
            conditionStrings.push('<span style="color:#aaa;margin-right:5px">' + value + '</span>' + queryFields[key].replace(/%/g, ''))
          }
        }
      }

      if (conditionStrings.length === 0) {
        return ''
      } else {
        return '搜尋條件：' + conditionStrings.join('<span style="color:#aaa;padding:0 5px">+</span>') + '，'
      }
    }
  },

  mounted() {
    if (this.$route.query.tfnamecode !== undefined) {
      this.$store.commit("search/resetVertexes");
      this.$store.commit("search/resetSpecimenMarkers");
      this.$store.commit("search/setFields", { tf_name_code: this.$route.query.tfnamecode })
    } else {
      this.$store.commit("search/deleteField", 'tf_name_code');
    }

    this.$store.commit("search/setSortBy", this.sortBy);
    this.$store.commit("search/setPageNumber", 1);

    this.startSearch().then(() => {
      if (this.isGetAllPositions) {
        // 取得全部標本位置並更新地圖上的標本標示
        this.$store
          .dispatch("search/getSpecimenPositions", this.maxPositionCounts)
          .then(() => {
            if (this.$refs.resultMap !== undefined) {
              this.$refs.resultMap.updateMarkers();
            }
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        // 更新地圖上的標本標示為目前分頁標本
        this.updatePaginationMarkers();
      }
    });

    window.scrollTo(0, 0);
  },

  methods: {
    updatePaginationMarkers() {
      // 準備分頁的標本地圖標示的資料
      const positions = [];
      this.pagination.data.forEach((specimenData) => {
        positions.push({
          id: specimenData.id,
          gmap_latitude: specimenData.gmap_latitude,
          gmap_longitude: specimenData.gmap_longitude,
        });
      });
      this.$store.commit("search/setSpecimenMarkers", positions);

      if (this.$refs.resultMap !== undefined) {
        this.$refs.resultMap.updateMarkers();
      }
    },

    switchToListMode(isListMode) {
      this.isListMode = isListMode;
    },

    sortByChanged(event) {
      this.$store.commit("search/setSortBy", event.target.value);
      this.$store.commit("search/setPageNumber", 1);

      // 重新搜尋顯示第一頁結果
      this.$refs.paginationComponent.showPage(1);
    },

    gotoAdvancedSearchPage() {
      this.$store.commit("search/deleteSimpleSearchName");
      this.$router.push("search-advanced");
    },

    showDownloadResultDialog() {
      this.exportOpen = !this.exportOpen;
    },

    pageChanged(pageNumber) {
      this.$store.commit("search/setPageNumber", pageNumber);

      this.startSearch().then(() => {
        if (this.isGetAllPositions === false) {
          this.updatePaginationMarkers();
        }
      });
    },

    async startSearch() {
      // search specimens
      this.pagination = await this.$store
        .dispatch("search/getSpecimens")
        .then((response) => {
          return response;
        })
        .catch((error) => {
          console.log(error);
        });

      // Set total pages for ExportDialog
      this.$store.commit(
        "search/setTotalPages",
        this.pagination.meta.pagination.total_pages
      );
    },

    closeDialog() {
      this.exportOpen = false;
    },
  },
};
</script>

<style>
@media (min-width: 1536px) {
  .pagination-bg-2xl {
    width: 100%;
    height: 300px;
    background-image: url("../../assets/pagination-bg-over1536.jpg");
  }
}

@media (min-width: 1024px) and (max-width: 1535.98px) {
  .pagination-bg-lg {
    width: 100%;
    height: 300px;
    background-image: url("../../assets/pagination-bg-over1024.jpg");
  }
}

@media (min-width: 640px) and (max-width: 1023.98px) {
  .pagination-bg-sm {
    width: 100%;
    height: 300px;
    background-image: url("../../assets/pagination-bg-over640.jpg");
  }
}

@media (max-width: 639.98px) {
  .pagination-bg {
    width: 100%;
    height: 250px;
    background-image: url("../../assets/pagination-bg-under640.jpg");
  }
}

.web-color {
  color: #64859d;
}

.web-bg-color {
  background-color: #64859d;
}

.web-dark-bg-color {
  background-color: #405d73;
}

a:hover {
  @apply web-color;
}

.text-color-blue {
  @apply web-color;
}

.table-button {
  @apply web-bg-color rounded px-2 sm:px-4 h-9 text-white;
}

.table-button:hover {
  @apply web-dark-bg-color rounded px-2 sm:px-4 h-9 text-white;
}

.table-icon {
  @apply w-10 h-10;
}

.footer-bg {
  width: 100%;
  height: 10px;
  background-image: url("../../assets/footer-bg.svg");
}

.searchDialog {
  position: fixed;
  top: 50%;
  left: 0px;
  margin-top: -310px;
}

.exportDialog {
  position: fixed;
  top: 50%;
  left: 0px;
  margin-top: -225px;
}

.slide-fade-enter-active {
  transition: all 0.5s ease;
}
.slide-fade-leave-active {
  transition: all 0.5s ease;
}
.slide-fade-enter-from {
  transform: translateY(-5%);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translateY(-5%);
  opacity: 0;
}
</style>
